import {
  getEnvCurrencyFromData,
  getEnvInputSeparators,
  onKeyDown
} from '@common/utils'
import {
  MolsonBox,
  MolsonIcon,
  MolsonTextField,
  MolsonTypography
} from '@components/reusable'
import { type ChangeEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { NumericFormat } from 'react-number-format'
import type { Option, SaveFn } from './CustomFit'
import { StyledCustomFitContainer } from './styles'
import { toNumber, validateName } from './validations'

export type FitItemProps = {
  customFitsList: Option[]
  fitItem: Option
  index: number
  onRemoveFit: (id?: number) => void
  onSave?: SaveFn
  onSetFitList: (options: Option[]) => void
}

export const MIN_LENGTH = 3
export const MAX_LENGTH = 100

export const FitItem = ({
  customFitsList,
  fitItem,
  index,
  onRemoveFit,
  onSave,
  onSetFitList
}: FitItemProps) => {
  const [validationError, setValidationError] = useState<string>('')

  const { decimal, thousands } = getEnvInputSeparators()

  const { t } = useTranslation('', {
    keyPrefix: 'common'
  })

  const handleInputChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    fitId: number,
    isNewValue: boolean
  ) => {
    const {
      target: { name, value }
    } = event

    const nameField = 'name'
    const isUpdatingField = Boolean(!isNewValue)

    if (name === nameField) {
      const error = validateName(String(value), t, MIN_LENGTH, MAX_LENGTH)

      setValidationError(
        !value && isUpdatingField
          ? String(
              t('fieldTooShort', {
                minLength: MIN_LENGTH
              })
            )
          : error
      )
    }

    const updatedFitsList = customFitsList.map(fit =>
      fit.id === fitId ? { ...fit, [name]: value } : fit
    )
    onSetFitList(updatedFitsList)
  }

  const handleOnBlur = async (
    value?: null | string,
    dnp?: number | string,
    id?: number | string,
    isNewValue?: boolean
  ) => {
    const isUpdatingField = Boolean(!isNewValue)

    const updatedFitsList = customFitsList.map(fit => {
      if (fit.id === id) {
        return {
          ...fit,
          dnp: dnp ? (dnp ?? 0) : ''
        }
      }
      return fit
    })

    onSetFitList(updatedFitsList)

    if (!(isUpdatingField || value)) {
      return
    }

    if (!value && isUpdatingField) {
      setValidationError(String(t('fieldTooShort', { minLength: MIN_LENGTH })))
      return
    }

    const error = validateName(String(value), t, MIN_LENGTH, MAX_LENGTH)

    if (error) {
      setValidationError(error)
    } else {
      const isUpdatingField = Boolean(!isNewValue)

      onSave?.({
        dnp: dnp ? toNumber(dnp) : 0,
        name: value,
        isUpdating: isUpdatingField,
        optionId: isUpdatingField ? Number(id) : undefined
      })
      setValidationError('')
    }
  }

  return (
    <MolsonBox display="flex" flexDirection="column">
      <StyledCustomFitContainer>
        <MolsonTextField
          error={!!validationError}
          name="name"
          onBlur={() =>
            handleOnBlur(
              fitItem.name,
              fitItem.dnp,
              fitItem.id,
              fitItem.isNewValue
            )
          }
          onChange={e =>
            handleInputChange(
              e,
              Number(fitItem.id),
              Boolean(fitItem?.isNewValue)
            )
          }
          sx={{ width: 300 }}
          value={fitItem.name}
        />

        <NumericFormat
          InputProps={{ startAdornment: getEnvCurrencyFromData() }}
          allowNegative={true}
          autoComplete="off"
          customInput={MolsonTextField}
          decimalSeparator={decimal}
          name="dnp"
          onBlur={() => handleOnBlur(fitItem.name, fitItem.dnp, fitItem.id)}
          onChange={e =>
            handleInputChange(
              e,
              Number(fitItem.id),
              Boolean(fitItem?.isNewValue)
            )
          }
          onKeyDown={onKeyDown}
          sx={{ width: 100 }}
          thousandSeparator={thousands}
          value={fitItem.dnp}
        />

        {(index !== 0 || Number(fitItem.id) < 0) && (
          <MolsonIcon
            variant="TRASH_CONTAINED"
            onClick={() => onRemoveFit(fitItem.id)}
            iconColor="error"
            fontSize="small"
            sx={{ ml: 1.5, cursor: 'pointer' }}
          />
        )}
      </StyledCustomFitContainer>

      {!!validationError && (
        <MolsonTypography variant="error">
          {String(validationError)}
        </MolsonTypography>
      )}
    </MolsonBox>
  )
}
